/**
 * SQAComplianceCard — SQA summary for Platform Dashboard
 * Phase 4.7.5 (WQ-170)
 *
 * Compact card showing IL breakdown, confidence gauge, NCR counts.
 * Sits alongside Laws Compliance in the Tier 2 bottom row.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getPlatformSummary, type PlatformSummary } from '../../../services/sqaService';

const IL_COLORS: Record<string, string> = {
  'IL-4': '#dc2626',
  'IL-3': '#ea580c',
  'IL-2': '#3b82f6',
  'IL-1': '#6b7280',
};

export interface SQAComplianceCardProps {
  className?: string;
}

export const SQAComplianceCard: React.FC<SQAComplianceCardProps> = ({ className }) => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<PlatformSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    getPlatformSummary(authFetch).then(setSummary).catch(() => {}).finally(() => setLoading(false));
    const interval = setInterval(() => {
      getPlatformSummary(authFetch).then(setSummary).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [authFetch, authLoading]);

  const confidence = summary?.overall_confidence ?? 0;
  const confidencePct = Math.round(confidence * 100);
  const confidenceColor = confidencePct >= 85 ? '#22c55e' : confidencePct >= 70 ? '#eab308' : '#dc2626';

  return (
    <div
      className={`card card-elevated ${className || ''}`}
      style={{ padding: 'var(--space-4)' }}
    >
      <h3 style={{
        margin: '0 0 var(--space-3)',
        fontSize: 'var(--text-lg, 18px)',
        color: 'var(--text-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        SQA Compliance
      </h3>

      {loading || !summary ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
          {loading ? 'Loading...' : 'No SQA data available'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* Confidence gauge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: 'var(--glass-bg)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: `3px solid ${confidenceColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 700,
              color: confidenceColor,
              flexShrink: 0,
            }}>
              {confidencePct}%
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                Overall Confidence
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {summary.total_components} components assessed
              </div>
            </div>
          </div>

          {/* IL breakdown */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '6px',
          }}>
            {Object.entries(IL_COLORS).map(([il, color]) => (
              <div
                key={il}
                style={{
                  textAlign: 'center',
                  padding: '8px 4px',
                  borderRadius: '6px',
                  background: `${color}12`,
                  border: `1px solid ${color}33`,
                }}
              >
                <div style={{ fontSize: '18px', fontWeight: 700, color }}>{summary.by_integrity_level[il] || 0}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{il}</div>
              </div>
            ))}
          </div>

          {/* NCR count */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            background: 'var(--glass-bg)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Open NCRs</span>
            <span style={{
              fontSize: '16px',
              fontWeight: 700,
              color: summary.open_ncrs > 0 ? '#ea580c' : '#22c55e',
            }}>
              {summary.open_ncrs}
            </span>
          </div>

          {/* Threshold violations */}
          {(summary.threshold_violation_count ?? 0) > 0 && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(234, 88, 12, 0.08)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid rgba(234, 88, 12, 0.2)',
            }}>
              <div style={{ fontSize: '12px', color: '#ea580c', fontWeight: 600, marginBottom: '2px' }}>
                {summary.threshold_violation_count} Below Threshold
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Components below IL minimum confidence
              </div>
            </div>
          )}

          {/* Link to SQA Dashboard */}
          <div
            onClick={() => navigate('/ai-command-center')}
            style={{
              fontSize: '12px',
              color: 'var(--accent-primary, #3b82f6)',
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            View SQA Dashboard &rarr;
          </div>

          {/* Last assessment date */}
          {summary.last_assessment_date && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
              Last assessed: {new Date(summary.last_assessment_date).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SQAComplianceCard;
